import { getErrorReporter } from "@kaggle/components";
import { createBufferedPort } from "./buffered-port";
import {
  BATCH_MESSAGE,
  BufferedPort,
  HANDSHAKE_ACK,
  HANDSHAKE_INIT,
  MessageListener,
  SCRIPT_ERROR,
} from "./types";

const portMap: Map<Window, BufferedPort> = new Map<Window, BufferedPort>();

const getBufferedPort = (frame: Window) => {
  let bufferedPort = portMap.get(frame);
  if (bufferedPort) {
    return bufferedPort;
  } else {
    bufferedPort = createBufferedPort();
    portMap.set(frame, bufferedPort);
    return bufferedPort;
  }
};

const isInsideIframe = () => {
  return window.location !== window.parent.location;
};

/*
 * Synchronously set up a window error handler to notify the parent
 * about any errors that might prevent startup.
 */
if (isInsideIframe()) {
  window.addEventListener("error", (e: ErrorEvent) => {
    getErrorReporter()?.report(e.error);

    // Ignore this error, it's happening when JuptyerLab tries to remove its hidden splash screen, it doesn't cause any real issues with the page
    // and should not move the session to ERROR state.
    if (
      e.message.includes("The node to be removed is not a child of this node")
    ) {
      // tslint:disable-next-line:no-console
      console.warn("Splash screen error was hidden: ", e.message);
      return;
    }
    window.parent.postMessage(
      {
        type: SCRIPT_ERROR,
        message: e.message,
      },
      "*"
    );
  });

  // Synchronously notify the parent window that the initial iframe request
  // has loaded. If the parent doesn't see this message, it will notify
  // the user that something went wrong loading the frame.
  window.parent.postMessage({ type: "LOADED" }, "*");
}

/**
 * Sets up a listener to accept handshake requests from iframes, should be
 * called before any iframes are created to ensure that it doesn't miss any
 * requests.
 */
export const handshakeAsHost = (w: Window = window): Promise<void> => {
  return new Promise((resolve, reject) => {
    w.addEventListener("message", (msgEvent: MessageEvent) => {
      if (hostHandshakeListener(msgEvent)) {
        resolve();
      } else if (msgEvent.data.type === SCRIPT_ERROR) {
        reject(msgEvent.data.message);
      }
    });
  });
};

const hostHandshakeListener = (msgEvent: MessageEvent) => {
  // We expect a message from all iframes informing us of their existence
  // and readiness to accept messages.
  if (msgEvent.data.type === HANDSHAKE_INIT) {
    const messagePort = msgEvent.ports[0];
    if (!msgEvent.source) {
      throw new Error("handshake initiated by undefined source");
    }
    const bufferedPort = getBufferedPort(msgEvent.source as WindowProxy);

    bufferedPort.internalSetPort(messagePort);
    messagePort.addEventListener("message", handleIncomingMessages);
    messagePort.start();
    bufferedPort.setHandshakeComplete();

    messagePort.postMessage({
      type: HANDSHAKE_ACK,
    });
    return true;
  } else {
    return false;
  }
};

// Modifiable so tests can inject their own implementation.
let createMessageChannel = () => new MessageChannel();

export const handshakeAsIframe = (parent: Window, self: Window = window) => {
  let messagePort: MessagePort;
  const bufferedPort = createBufferedPort();
  portMap.set(parent, bufferedPort);

  // Allocate some ports for the communication channel between the host frame
  // and ourselves.
  const channel = createMessageChannel();
  messagePort = channel.port1;

  bufferedPort.internalSetPort(messagePort);
  messagePort.addEventListener("message", handleIncomingMessages);

  // Setup to receive acknowledgment from parent frame.
  messagePort.addEventListener(
    "message",
    function clientHandshakeListener(msgEvent: MessageEvent) {
      if (msgEvent.data.type === HANDSHAKE_ACK) {
        bufferedPort.setHandshakeComplete();
        self.removeEventListener("message", clientHandshakeListener);
      }
    }
  );

  messagePort.start();

  // Initiate handshake.
  parent.postMessage(
    {
      type: HANDSHAKE_INIT,
    },
    "*",
    [channel.port2]
  );
};

/**
 * Sends a message to the specified iframe. Analogous to postMessage in
 * browser cross-frame messaging API.
 * @param targetWindow the intended recipient
 * @param payload the message to send
 */
export const sendMessage = (targetWindow: Window, payload: any) => {
  const bufferedPort = getBufferedPort(targetWindow);
  bufferedPort.postMessage(payload);
};

let messageListeners: MessageListener[] = [];

/**
 * Adds a listener to be informed when a message is received. Analogous to
 * addEventListener("message", () => {...}).
 * @param listener handler function for messages
 */
export const addMessageListener = (listener: MessageListener) => {
  messageListeners.push(listener);
};

/**
 * Disconnects the listener from being notified when a message is received.
 * Analogous to removeEventListener("message", () => {...}).
 * @param listener handler to be disconnected
 */
export const removeMessageListener = (listener: MessageListener) => {
  messageListeners = messageListeners.filter(l => l !== listener);
};

const handleIncomingMessages = (event: MessageEvent) => {
  if (event.data.type !== BATCH_MESSAGE) {
    return;
  }
  const messages: any[] = event.data.messages;

  messages
    .map(payload => ({
      ...event,
      data: payload,
    }))
    .forEach((message: MessageEvent) => {
      messageListeners.forEach(listener => {
        listener(message);
      });
    });
};

/**
 * Resets the state of the transport
 */
const reset = () => {
  messageListeners = [];
  portMap.clear();
  createMessageChannel = () => new MessageChannel();
};

/**
 * Sends synthetic event, useful for testing.
 * @param event the message to propagate to listeners
 */
const fireSyntheticMessage = (
  data: any = [{ debug: "this is a synthetic event" }]
) => {
  const event = new MessageEvent("message", {
    data: {
      type: BATCH_MESSAGE,
      messages: data,
    },
  });
  handleIncomingMessages(event);
};

/**
 * MessageChannel doesn't appear to be present in the jest runtime, this allows
 * us to pass mock implementations of MessageChannel to the test if we in fact
 * rely on them, which appears to only be the case for tests of the handshake
 * method.
 * @param impl implementation of message channel creation
 */
const overrideMessageChannelImplementation = (impl: () => MessageChannel) => {
  createMessageChannel = impl;
};

export const testHelpers = {
  reset,
  fireSyntheticMessage,
  fireSyntheticHostHandshakeRecieved: hostHandshakeListener,
  overrideMessageChannelImplementation,
};
